import { authProxyApi } from '@verifime/auth-proxy-definition';
import { retrieveTokens } from 'amazon-cognito-passwordless-auth/storage';
import { parseJwtPayload } from 'amazon-cognito-passwordless-auth/util';
import { CognitoIdTokenPayload } from 'amazon-cognito-passwordless-auth/jwt-model';
import { clearStoredTokens } from './token-storage';

/**
 * Extracts and logs structured error codes from auth proxy HTTP error responses.
 * The proxy returns { error: "AUTH_FAILED" | ... } in the response body.
 * Zodios/Axios wraps this in an AxiosError with the detail buried in
 * error.response.data.error.
 */
export function logProxyError(context: string, err: unknown): void {
  const detail = extractProxyErrorCode(err);
  console.error(`[auth-proxy] ${context}:`, detail ?? summariseErrorForLogging(err));
}

/** Allowlisted fields safe to log for an unclassified error. */
export interface RedactedErrorSummary {
  name?: string;
  message?: string;
  status?: number;
}

/**
 * Redacts an error down to the specific fields worth having for diagnostics:
 * its `name`, its `message`, and the HTTP status if present. Never the raw
 * error object, never `config`, never `request`, never `response.data`.
 *
 * Redaction here follows an allowlist, not a denylist: for a magic-link
 * failure, an axios-shaped error's `config.data` carries the serialised
 * request body verbatim, which for `postAuthrespond` is the magic-link
 * fragment itself, a bearer credential. A guessed denylist of secret-sounding
 * keys would miss that field (and any other we didn't think to name), so
 * only the fields explicitly proven safe are projected out.
 */
export function summariseErrorForLogging(err: unknown): RedactedErrorSummary {
  if (!err || typeof err !== 'object') return {};

  const name = 'name' in err && typeof err.name === 'string' ? err.name : undefined;
  const message = 'message' in err && typeof err.message === 'string' ? err.message : undefined;
  const status =
    'response' in err &&
    err.response &&
    typeof err.response === 'object' &&
    'status' in err.response &&
    typeof err.response.status === 'number'
      ? err.response.status
      : undefined;

  return { name, message, status };
}

export function extractProxyErrorCode(err: unknown): string | undefined {
  if (err && typeof err === 'object' && 'response' in err) {
    return (err as { response?: { data?: { error?: string } } }).response?.data?.error;
  }
  return undefined;
}

function extractHttpStatus(err: unknown): number | undefined {
  if (err && typeof err === 'object' && 'response' in err) {
    return (err as { response?: { status?: number } }).response?.status;
  }
  return undefined;
}

/** True only for a refresh failure that can never succeed on retry. */
export function isTerminalRefreshFailure(err: unknown): boolean {
  if (extractProxyErrorCode(err) === 'AUTH_FAILED') return true;
  return extractHttpStatus(err) === 401;
}

/**
 * Seconds of grace after the access token lapses during which a transient
 * failure is still retried.
 *
 * Refresh tokens live 8 hours against the access token's 60 minutes, so
 * recovery stays possible long past expiry, and the only cost of retrying is
 * background traffic. 5 minutes covers a lift, a tunnel or a wifi handover;
 * past that the customer has reloaded or left, so further polling buys a
 * recovery nobody is present to receive (about 10 attempts at the existing
 * 30s interval).
 */
export const TRANSIENT_RETRY_GRACE_SECONDS = 300;

// --- Token refresh via auth proxy ---

interface ProxyRefreshTokens {
  accessToken: string;
  idToken: string;
  expireAt: Date;
  username: string;
}

interface ProxyRefreshArgs {
  abort?: AbortSignal;
  tokensCb?: (tokens: ProxyRefreshTokens) => void | Promise<void>;
  isRefreshingCb?: (isRefreshing: boolean) => void;
  /** Called once a terminal refresh failure has cleared the stored session. */
  tokensRemovedCb?: () => void;
}

// Promise-based deduplication: concurrent callers share the in-flight promise
let refreshPromise: Promise<ProxyRefreshTokens | void> | null = null;

/**
 * Refresh tokens via the auth proxy POST /auth/refresh endpoint.
 * Concurrent calls share the in-flight promise (deduplication).
 * Returns the refreshed tokens on success.
 */
export async function refreshTokensViaProxy(
  args: ProxyRefreshArgs,
): Promise<ProxyRefreshTokens | void> {
  // Share in-flight promise with concurrent callers
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const stored = await retrieveTokens();
    if (!stored?.refreshToken) {
      console.debug('[auth-proxy] No refresh token in storage, skipping refresh');
      return;
    }

    args.isRefreshingCb?.(true);
    try {
      const portal = process.env.NEXT_PUBLIC_AUTH_PORTAL_ID || undefined;
      const result = await authProxyApi.postAuthrefresh(
        { refreshToken: stored.refreshToken, portal, accessToken: stored.accessToken },
        args.abort ? { signal: args.abort } : undefined,
      );

      const username = parseJwtPayload<CognitoIdTokenPayload>(result.idToken)['cognito:username'];
      if (!username) {
        throw new Error('ID token from proxy missing cognito:username claim');
      }

      const newTokens: ProxyRefreshTokens = {
        accessToken: result.accessToken,
        idToken: result.idToken,
        expireAt: new Date(Date.now() + result.expiresIn * 1000),
        username,
      };

      await args.tokensCb?.(newTokens);
      return newTokens;
    } catch (err) {
      logProxyError('Token refresh failed', err);
      throw err;
    } finally {
      args.isRefreshingCb?.(false);
    }
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

/**
 * Schedule a token refresh via proxy, firing ~30 seconds before expiry
 * with +/-15s jitter (matching SDK behaviour).
 *
 * Resolves immediately after setting the timer (not after the refresh fires),
 * matching the SDK's scheduleRefresh() semantics so isSchedulingRefresh
 * transitions to false promptly.
 *
 * Re-schedules recursively on success. On failure, retries after 30 seconds.
 */
export function scheduleRefreshViaProxy(args: ProxyRefreshArgs): Promise<void> {
  return retrieveTokens()
    .then((stored) => {
      if (!stored?.expireAt) {
        console.debug('[auth-proxy] No expireAt in stored tokens, skipping refresh schedule');
        return;
      }

      if (args.abort?.aborted) return;

      const msUntilExpiry = stored.expireAt.getTime() - Date.now();
      // Refresh ~30 seconds before expiry with +/-15s jitter (matching SDK)
      const jitter = (Math.random() - 0.5) * 30_000;
      const delay = Math.max(0, msUntilExpiry - 30_000 + jitter);

      const timer = setTimeout(() => {
        refreshTokensViaProxy(args)
          .then(() => {
            // Re-schedule on success (recursive, like SDK)
            scheduleRefreshViaProxy(args);
          })
          .catch(async (err) => {
            // Error already logged by logProxyError.
            if (args.abort?.aborted) return;

            if (isTerminalRefreshFailure(err)) {
              // The refresh token itself is dead: no amount of retrying will
              // ever succeed, so stop the loop and drop the dead session.
              try {
                await clearStoredTokens();
              } catch (clearErr) {
                // This chain is orphaned inside a setTimeout callback: nothing else
                // awaits or observes it. Without this catch, a rejection here (e.g.
                // a storage.removeItem failure) becomes an unhandled promise
                // rejection, skips tokensRemovedCb below, and can leave storage
                // partially cleared (Promise.all races all removeItem calls
                // concurrently), including LastAuthUser, which would make a future
                // clearStoredTokens() short-circuit before it ever finds the
                // orphaned remainder.
                logProxyError(
                  'Failed to clear stored tokens after terminal refresh failure',
                  clearErr,
                );
              }
              console.info(
                '[auth-proxy] Terminal refresh failure, session cleared',
                summariseErrorForLogging(err),
              );
              // Notify React regardless of whether storage clearing fully succeeded: a
              // half-cleared storage must never leave React believing the session is
              // still good, and a subsequent sign-in overwrites storage anyway.
              args.tokensRemovedCb?.();
              return;
            }

            // Transient failure: keep retrying at the existing 30s interval,
            // but only while the session could still plausibly be recovered.
            const graceDeadline =
              stored.expireAt.getTime() + TRANSIENT_RETRY_GRACE_SECONDS * 1000;
            if (Date.now() > graceDeadline) {
              // Deliberately no clear and no callback here: see TD-059
              // (docs/technical-debt.md) for the recorded gap this leaves.
              console.info(
                '[auth-proxy] Transient refresh retries exhausted after grace window, giving up',
              );
              return;
            }

            const retryTimer = setTimeout(() => scheduleRefreshViaProxy(args), 30_000);
            args.abort?.addEventListener('abort', () => clearTimeout(retryTimer));
          });
      }, delay);

      args.abort?.addEventListener('abort', () => clearTimeout(timer));
      // Resolves immediately after scheduling (not after refresh fires)
    })
    .catch((err) => {
      logProxyError('Failed to read tokens for scheduling refresh', err);
    });
}
